import deburr from 'lodash/deburr';
import isNil from 'lodash/isNil';
import takeWhile from 'lodash/takeWhile';
import { DateTime } from 'luxon';

import type { User } from '../schema/admin';

import { isNotEmptyString } from './checks';
import type { Nullable } from './object';

type WithNamePrefixReturnType<
  Prefix extends Nullable<string>, FieldName extends string,
> = string extends Prefix
  ? `${FieldName}`
  : undefined extends Prefix
    ? `${FieldName}`
    : `${Prefix}.${FieldName}`;

export const withNamePrefix = <Prefix extends Nullable<string>, FieldName extends string>(
  prefix: Prefix, name: FieldName,
): WithNamePrefixReturnType<Prefix, FieldName> => (
  isNotEmptyString(prefix)
    ? `${prefix}.${name}` : `${name}`
) as WithNamePrefixReturnType<Prefix, FieldName>;

export const normalizeForSearch = (value: string): string => deburr(value).trim().toLocaleLowerCase();

export const stringGroupRepresentation = (str: string): string => {
  if (/^[a-z]/i.test(deburr(str))) {
    return deburr(str[0]).toLocaleUpperCase();
  }

  if (/^\d/.test(str)) {
    return '0-9';
  }

  return '#';
};

export const nonUniqueUsernameFromFirstAndLastName = (user: Pick<User, 'firstName' | 'lastName'>): string => `${user.firstName.trim()[0] || ''}${user.lastName.trim()}`.replace(/\s+/g, '-');
export const usernameFromEmail = (user: Pick<User, 'email'>): string => user.email.split('@')[0];

export const parseURLDateParts = (year: string, month?: string, day?: string): Nullable<Date> => {
  const unparsedParts: [Nullable<string>, string][] = takeWhile([
    [year, 'yyyy'],
    [month, 'M'],
    [day, 'd'],
  ], ([value]) => !isNil(value));

  const parts = unparsedParts.reduce<[string, string][]>((prevParts, currentPart) => {
    const [currentPartValue, currentPartKey] = currentPart;
    if (isNil(currentPartValue)) {
      return prevParts;
    }

    return [...prevParts, [currentPartValue, currentPartKey]];
  }, []);

  const current = DateTime.fromFormat(parts.map(([value]) => value).join('-'), parts.map(([, key]) => key).join('-'));

  if (current.isValid) {
    return current.toJSDate();
  }

  return null;
};

export interface Hashable { toString: () => string }
export const hashCode = (el: Hashable): string => {
  const str = el.toString();
  let hash = 0;
  for (let i = 0, len = str.length; i < len; i += 1) {
    const chr = str.charCodeAt(i);

    hash = (hash << 5) - hash + chr;

    hash |= 0; // Convert to 32bit integer
  }
  return hash.toString();
};

export const hashCodeWithVersion = (el: Hashable, version: number) => {
  const str = `${el.toString()}-${version}`
  return hashCode(str);
}

export const makeValidURL = (domain: string, subpage: string) => [
  domain.replace(/\/$/g, ''),
  subpage.replace(/^\//g, '')
].join('/')

export const trimToLength = (data: string, length: number) => `${data.slice(0, length)}${data.length > length ? '…' : ''}`

export const toLowerCase = <T extends string>(arg: T): Lowercase<T> => arg.toLowerCase() as Lowercase<T>
